import Axios from '@/utils/axios';
import { getBackendErrorMessage } from '@/utils/errorHandler';
import { unwrapList } from '@/utils/apiEnvelope';

// ─── Enums ───────────────────────────────────────────────────────────────────

export enum AlertType {
  TELEMETRY_STALE = 'TELEMETRY_STALE',
  // Dash Rent escalation — who is @-mentioned at each moment ops must act.
  RENT_SHUTDOWN_PENDING = 'RENT_SHUTDOWN_PENDING',
  RENT_SHUTDOWN_DONE = 'RENT_SHUTDOWN_DONE',
  RENT_REPOSSESSION_CREATED = 'RENT_REPOSSESSION_CREATED',
  RENT_REPOSSESSION_FAILED = 'RENT_REPOSSESSION_FAILED',
  // Jeda Tagihan: sent as a DM to each person on the list, not a mention.
  RENT_BILLING_PAUSE = 'RENT_BILLING_PAUSE',
}

/** What each Slack mention list is for, in ops' words. */
export const ALERT_TYPE_DESCRIPTIONS: Record<string, string> = {
  [AlertType.TELEMETRY_STALE]:
    'PIC yang di-mention saat telemetry unit berhenti masuk.',
  [AlertType.RENT_SHUTDOWN_PENDING]:
    'Unit wajib dimatikan manual (tunggakan lewat ambang shutdown), termasuk digest harian unit yang belum dimatikan.',
  [AlertType.RENT_SHUTDOWN_DONE]:
    'Konfirmasi ops bahwa unit sudah dimatikan (aksi Tandai Shutdown Selesai).',
  [AlertType.RENT_REPOSSESSION_CREATED]:
    'Case repossession baru dibuat, otomatis maupun manual (channel repo).',
  [AlertType.RENT_REPOSSESSION_FAILED]:
    'Auto-create repossession gagal, atau permintaan buat case manual saat auto-create mati.',
  [AlertType.RENT_BILLING_PAUSE]:
    'Dikirim lewat DM Slack ke tiap orang di daftar: tagihan rental dijeda / dilanjutkan, dan digest harian jeda yang perlu dicek. Kalau tak ada DM yang terkirim (daftar kosong atau DM ditolak), dikirim ke channel rent.',
};

export enum VehicleAlertStatus {
  OPEN = 'OPEN',
  RESOLVED = 'RESOLVED',
}

export const ALERT_TYPE_LABELS: Record<string, string> = {
  [AlertType.TELEMETRY_STALE]: 'Telemetry Stale',
  [AlertType.RENT_SHUTDOWN_PENDING]: 'Rent: Unit Wajib Dimatikan',
  [AlertType.RENT_SHUTDOWN_DONE]: 'Rent: Unit Dimatikan',
  [AlertType.RENT_REPOSSESSION_CREATED]: 'Rent: Case Repossession Dibuat',
  [AlertType.RENT_REPOSSESSION_FAILED]: 'Rent: Auto-create Repossession Gagal',
  [AlertType.RENT_BILLING_PAUSE]: 'Rent: Jeda Tagihan (DM)',
};

export const getAlertTypeLabel = (value?: string | null): string => {
  if (!value) return '-';
  return (
    ALERT_TYPE_LABELS[value] ||
    value
      .toString()
      .replace(/_/g, ' ')
      .replace(/\b\w/g, (char) => char.toUpperCase())
  );
};

// ─── Interfaces ───────────────────────────────────────────────────────────────

export interface VehicleAlert {
  ID: string;
  vehicleID: string;
  platNomor: string | null;
  businessUnit: string | null;
  alertType: string;
  status: VehicleAlertStatus;
  oemID: string | null;
  oemName: string | null;
  modelID: string | null;
  modelName: string | null;
  latitude: number | null;
  longitude: number | null;
  firedAt: string;
  resolvedAt: string | null;
  durationMinutes: number;
  context: Record<string, unknown> | null;
  createdAt: string;
  createdBy: string;
  updatedAt: string | null;
  updatedBy: string | null;
}

export interface VehicleAlertListRequest {
  page?: string;
  size?: string;
  sort?: string;
  order?: 'asc' | 'desc';
  search?: string;
  alertTypes?: string;
  status?: string;
  vehicleIDs?: string;
  businessUnits?: string;
  oemIDs?: string;
  modelIDs?: string;
  firedAtStart?: string;
  firedAtEnd?: string;
}

export interface VehicleAlertPagination {
  total: number;
  page: number;
  size: number;
  lastPage: number;
}

export interface VehicleAlertListResponse {
  data: VehicleAlert[];
  openCount: number;
  resolvedCount: number;
  pagination: VehicleAlertPagination;
}

export interface VehicleAlertComment {
  ID: string;
  vehicleMonitoringID: string;
  comment: string;
  createdAt: string;
  createdBy: string;
  updatedAt: string | null;
  updatedBy: string | null;
}

export interface CreateVehicleAlertCommentRequest {
  comment: string;
  createdBy: string;
}

export interface UpdateVehicleAlertCommentRequest {
  comment: string;
  updatedBy: string;
}

// ─── API functions ────────────────────────────────────────────────────────────

export const getVehicleAlerts = async (
  params?: VehicleAlertListRequest
): Promise<VehicleAlertListResponse> => {
  try {
    const response = await Axios.get('/v1/monitoring', {
      params,
    });

    // The endpoint wraps its payload in an ApiResponse envelope:
    //   { status, data: { data: VehicleAlert[], openCount, resolvedCount,
    //   pagination }, pagination }
    // Unwrap defensively so we still work if the envelope is flattened.
    const body = response.data ?? {};
    const inner = body.data ?? body;
    const list: VehicleAlert[] = Array.isArray(inner)
      ? inner
      : (inner?.data ?? []);
    const pagination: VehicleAlertPagination = body.pagination ??
      inner?.pagination ?? {
        total: list.length,
        page: 1,
        size: list.length,
        lastPage: 1,
      };

    return {
      data: list,
      openCount: inner?.openCount ?? 0,
      resolvedCount: inner?.resolvedCount ?? 0,
      pagination,
    };
  } catch (error: unknown) {
    const backendMessage = getBackendErrorMessage(error);
    throw new Error(backendMessage);
  }
};

export const getVehicleAlertById = async (
  alertID: string
): Promise<VehicleAlert> => {
  try {
    const response = await Axios.get(`/v1/monitoring/${alertID}`);
    // Envelope: { status, data: VehicleAlert }
    return response.data?.data ?? response.data;
  } catch (error: unknown) {
    const backendMessage = getBackendErrorMessage(error);
    throw new Error(backendMessage);
  }
};

export const getVehicleAlertComments = async (
  alertID: string
): Promise<VehicleAlertComment[]> => {
  try {
    const response = await Axios.get(`/v1/monitoring/${alertID}/comments`);
    const data = response.data?.data ?? response.data;
    return Array.isArray(data) ? data : [];
  } catch (error: unknown) {
    const backendMessage = getBackendErrorMessage(error);
    throw new Error(backendMessage);
  }
};

export const createVehicleAlertComment = async (
  alertID: string,
  data: CreateVehicleAlertCommentRequest
): Promise<void> => {
  try {
    await Axios.post(`/v1/monitoring/${alertID}/comments`, data);
  } catch (error: unknown) {
    const backendMessage = getBackendErrorMessage(error);
    throw new Error(backendMessage);
  }
};

export const updateVehicleAlertComment = async (
  alertID: string,
  commentID: string,
  data: UpdateVehicleAlertCommentRequest
): Promise<void> => {
  try {
    await Axios.patch(`/v1/monitoring/${alertID}/comments/${commentID}`, data);
  } catch (error: unknown) {
    const backendMessage = getBackendErrorMessage(error);
    throw new Error(backendMessage);
  }
};

// ─── Alert assignees (Slack mentions) ────────────────────────────────────────
//
// Who gets @-mentioned on which Slack alert, scoped per (alertType,
// businessUnit); a row with no business unit is the fallback for units whose
// unit has no row of its own. Legacy `/v1` convention: the actor rides in the
// body, supplied by the page.

export interface AlertAssignee {
  ID: string;
  alertType: string;
  businessUnit: string | null;
  /** Slack member ID (U0123ABCD) — what `<@…>` needs, not the handle. */
  slackUserID: string;
  slackDisplayName: string;
  isActive: boolean;
  createdAt: string;
  createdBy: string;
  updatedAt: string | null;
  updatedBy: string | null;
}

export interface CreateAlertAssigneeRequest {
  alertType: AlertType;
  businessUnit?: string | null;
  slackUserID: string;
  slackDisplayName: string;
  isActive?: boolean;
  createdBy: string;
}

export interface UpdateAlertAssigneeRequest {
  alertType?: AlertType;
  /** '' or null clears the scope back to the fallback. */
  businessUnit?: string | null;
  slackUserID?: string;
  slackDisplayName?: string;
  isActive?: boolean;
  updatedBy: string;
}

const ALERT_ASSIGNEES = '/v1/monitoring/alert-assignees';

export const getAlertAssignees = async (): Promise<AlertAssignee[]> => {
  try {
    const response = await Axios.get(ALERT_ASSIGNEES);
    return unwrapList<AlertAssignee>(response.data);
  } catch (error: unknown) {
    throw new Error(getBackendErrorMessage(error));
  }
};

export const createAlertAssignee = async (
  data: CreateAlertAssigneeRequest
): Promise<{ ID: string }> => {
  try {
    const response = await Axios.post(ALERT_ASSIGNEES, data);
    return response.data?.data ?? response.data;
  } catch (error: unknown) {
    throw new Error(getBackendErrorMessage(error));
  }
};

export const updateAlertAssignee = async (
  id: string,
  data: UpdateAlertAssigneeRequest
): Promise<void> => {
  try {
    await Axios.patch(`${ALERT_ASSIGNEES}/${id}`, data);
  } catch (error: unknown) {
    throw new Error(getBackendErrorMessage(error));
  }
};

export const deleteAlertAssignee = async (id: string): Promise<void> => {
  try {
    await Axios.delete(`${ALERT_ASSIGNEES}/${id}`);
  } catch (error: unknown) {
    throw new Error(getBackendErrorMessage(error));
  }
};
